import { FC, useMemo, useState } from 'react'
import gql from 'graphql-tag'
import update from 'immutability-helper'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import {
  ChannelOrderField,
  OrderDirection,
  useGqlChannelSearchChannelListTabQuery,
} from 'generated/graphql'
import SearchResultHeader from '@app.components/SearchResultHeader'
import Select from '@app.components/inputs/Select'
import ChannelCardLoading from '@app.feature/channel/component/cards/ChannelCard/ChannelCardLoading'
import ChannelList from '@app.feature/channel/component/lists/ChannelList'
import useIntersect from '@app.modules/hooks/useIntersect'

gql`
  query gqlChannelSearchChannelListTab(
    $filterBy: ChannelFilter
    $orderBy: ChannelOrder
    $first: Int
    $after: String
  ) {
    channelFeed(
      filterBy: $filterBy
      orderBy: $orderBy
      first: $first
      after: $after
    ) {
      ...FollowList_ChannelConnection
    }
  }
`

const ChannelSearchChannel: FC = () => {
  const router = useRouter()
  const query = `${router.query.q || ``}`
  const [orderField, setOrderField] = useState(ChannelOrderField.FOLLOWER)

  const { data, loading, fetchMore } = useGqlChannelSearchChannelListTabQuery({
    variables: {
      filterBy: {
        q: query,
      },
      orderBy: {
        direction: OrderDirection.DESC,
        field: orderField,
      },
      first: 20,
    },
    notifyOnNetworkStatusChange: true,
  })

  const hasNextPage = useMemo(() => {
    if (!data || !data.channelFeed) return null
    return data.channelFeed.pageInfo.hasNextPage
  }, [data])

  const lastCursor = useMemo(() => {
    if (!data || !data.channelFeed) return null
    return data.channelFeed.pageInfo.endCursor
  }, [data])

  const handleIntersect = () => {
    if (!hasNextPage || !lastCursor || loading) return

    const variables = {
      filterBy: {
        q: query,
      },
      orderBy: {
        direction: OrderDirection.DESC,
        field: orderField,
      },
      first: 20,
      after: lastCursor,
    }

    fetchMore({
      variables,
      updateQuery: (previousQueryResult, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.channelFeed)
          return previousQueryResult
        const result = update(previousQueryResult, {
          channelFeed: {
            edges: {
              $push: fetchMoreResult.channelFeed.edges,
            },
            pageInfo: {
              $set: fetchMoreResult.channelFeed.pageInfo,
            },
          },
        })
        return result
      },
    })
  }

  const handleChange = (e) => {
    if (e.target.value === 'last-news') {
      setOrderField(ChannelOrderField.LAST_NEWS_ITEM_CREATED_AT)
    } else {
      setOrderField(ChannelOrderField.FOLLOWER)
    }
  }

  const target = useIntersect(handleIntersect, [lastCursor, loading, fetchMore])

  return (
    <StyledWrapper>
      <div className="header-wrapper">
        <SearchResultHeader>
          채널{' '}
          {data?.channelFeed?.totalCount
            ? `${data?.channelFeed.totalCount}건`
            : ``}
        </SearchResultHeader>
        <Select onChange={handleChange}>
          <option value="follower">팔로워순</option>
          <option value="last-news">최신순</option>
        </Select>
      </div>
      <ChannelList
        data={data?.channelFeed?.edges || []}
        loading={loading}
        skeletonSize={3}
      />
      {hasNextPage && <div ref={target}></div>}
      {hasNextPage && <ChannelCardLoading type="search" />}
      {!loading && !hasNextPage && data?.channelFeed?.edges.length !== 0 && (
        <div className="end-content">모든 채널을 확인했습니다.</div>
      )}
    </StyledWrapper>
  )
}

export default ChannelSearchChannel

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  .header-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .end-content {
    display: flex;
    justify-content: center;
    padding: 20px;
    color: #999999;
  }
`
